import { useState, useEffect } from 'react';
import {
  Button,
  Collapse,
  Row,
  Form,
  InputGroup,
  Col,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';

import cx from 'classnames';

import CompareIcon from '../CompareIcon';
import QAAddressMap from './components/QAAddressMap';

import CopyToClipboardIcon from 'components/CopyToClipboardIcon';
import { IcebergIcon } from '@tradesolution/iceberg-ui-react';
import AddressTypeahead from 'components/AddressTypeahead';

import { IAdresse } from 'components/AddressTypeahead/types';

import AdresseApi from 'services/KjederegisteretAdminApi/AdresseApi';
import {
  AddressSearchResult,
  EnhetAtAdresseDto,
} from 'services/KjederegisteretAdminApi/AdresseApi/types';

import { Coordinates } from 'models/Coordinates';

import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import { formatAddressAsOneLiner } from 'utils/AddressHelper';
import { CompareStatus, qaCompareSimpleValue } from '../../utils';

import Style from './index.module.css';
import { ChangeRequestValidator } from '../../ChangeRequestValidator';
import { AddressComparer } from '../../AddressComparer';

interface Props {
  changeRequestAddress: IAdresse;
  improvedAddress: IAdresse;
  onImprovedAddressChanged: (address: IAdresse) => void;
  isCustomAddress: boolean;
  changeRequestId: string;
  postadresse?: IAdresse;
  fakturaadresse?: IAdresse;
  leveringsadresse?: IAdresse;
  copyPostAdresse?: boolean;
  setCopyPostAdresse?: (checked: boolean) => void;
}

const QAAddressForm = (props: Props) => {
  const isMountedRef = useIsMountedRef();

  const [addressMapVisible, setAddressMapVisible] = useState(false);
  const [lookupCrResults, setLookupCrResults] = useState<AddressSearchResult[]>([]);
  const [validatedChangeRequestAddress, setValidatedChangeRequestAddress] =
    useState<AddressSearchResult>();
  const [isExpandRow, setIsExpandRow] = useState<boolean>(true);
  const [copyTouched, setCopyTouched] = useState<boolean>(false);
  const [crValueError, setCrValueError] = useState<string | null>(null);
  const [isLoadingCrValidation, setIsLoadingCrValidation] = useState<boolean>(false);
  // We keep a setter for active enheter at the besøksadresse for future display/use.
  // Skipping the state variable to avoid unused variable warnings.
  const [activeEnheterOnBesoksadresse, setActiveEnheterOnBesoksadresse] = useState<
    EnhetAtAdresseDto[]
  >([]);

  const getCompareStatus = (): CompareStatus => {
    if (!props.improvedAddress || !props.improvedAddress.gateadresse) {
      return 'ny';
    }
    if (!props.changeRequestAddress) {
      return 'ignorer';
    }
    const isEqual = AddressComparer.equals(props.changeRequestAddress, props.improvedAddress);
    if (isEqual) {
      return 'lik';
    }
    return 'ulik';
  };

  const [compareStatus, setCompareStatus] = useState<CompareStatus>(getCompareStatus());

  const improvedCoordinates =
    props.improvedAddress && props.improvedAddress?.latitude && props.improvedAddress?.longitude
      ? new Coordinates(props.improvedAddress?.latitude, props.improvedAddress?.longitude)
      : undefined;

  const crCoordinatesValidated =
    validatedChangeRequestAddress &&
    validatedChangeRequestAddress.latitude &&
    validatedChangeRequestAddress.longitude
      ? new Coordinates(
          validatedChangeRequestAddress.latitude,
          validatedChangeRequestAddress.longitude,
        )
      : undefined;

  const transferAddress = () => {
    if (validatedChangeRequestAddress) {
      setCopyTouched(false);
      props.onImprovedAddressChanged(validatedChangeRequestAddress);
    }
  };

  const lookupChangeRequestAddress = async (q: string) => {
    try {
      setIsLoadingCrValidation(true);
      const foundAddresses = await AdresseApi.search(q);
      if (isMountedRef.current) {
        setLookupCrResults(foundAddresses);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (isMountedRef.current) {
        setIsLoadingCrValidation(false);
      }
    }
  };

  useEffect(() => {
    if (props.changeRequestAddress && isMountedRef.current) {
      lookupChangeRequestAddress(formatAddressAsOneLiner(props.changeRequestAddress));
    }
  }, [props.changeRequestAddress?.gateadresse]);

  useEffect(() => {
    // look up if there are other active enheter on this address
    // https://kjederegisteretadminapi.tradesolution.no/api/Adresse/enheter/active?gateadresse=Str%C3%B8msveien%20245&postnr=0668
    const fetchActiveEnheter = async (gateadresse: string, postnr: string) => {
      try {
        const enheter = await AdresseApi.getActiveEnheter(gateadresse, postnr);
        if (isMountedRef.current) {
          setActiveEnheterOnBesoksadresse(enheter);
        }
      } catch (error) {
        console.error('Error fetching active enheter:', error);
      }
    };

    if (props.changeRequestAddress && isMountedRef.current) {
      fetchActiveEnheter(props.changeRequestAddress.gateadresse, props.changeRequestAddress.postnr);
    }
  }, [props.changeRequestAddress?.gateadresse, props.changeRequestAddress?.postnr, isMountedRef]);

  useEffect(() => {
    if (
      !copyTouched &&
      !props.copyPostAdresse &&
      props.changeRequestAddress &&
      props.postadresse &&
      props.improvedAddress &&
      props.changeRequestAddress.formattedForOneLine === props.postadresse.formattedForOneLine &&
      props.improvedAddress.formattedForOneLine !== props.changeRequestAddress.formattedForOneLine
    ) {
      props.setCopyPostAdresse(true);
    }
  }, [
    copyTouched,
    props.copyPostAdresse,
    props.changeRequestAddress,
    props.postadresse,
    props.improvedAddress,
    props.setCopyPostAdresse,
  ]);

  const checkAddressExists = (
    changeRequestAddress: IAdresse,
    lookupResults: AddressSearchResult[],
  ): boolean => {
    const formattedAddress = formatAddressAsOneLiner(changeRequestAddress).toUpperCase();
    return lookupResults.some(
      (result) => result.formattedForOneLine.toUpperCase() === formattedAddress,
    );
  };

  useEffect(() => {
    // Check if the address from the change request exists in the lookup results.
    const addressExists = checkAddressExists(props.changeRequestAddress, lookupCrResults);

    if (isMountedRef.current) {
      // If the address exists, find its index in the lookup results to set the validated address.
      if (addressExists) {
        const formattedChangeRequestAddress = formatAddressAsOneLiner(
          props.changeRequestAddress,
        ).toUpperCase();
        const index = lookupCrResults.findIndex(
          (result) => result.formattedForOneLine.toUpperCase() === formattedChangeRequestAddress,
        );

        // If a valid index is found, use it to set the validated change request address.
        if (index !== -1) {
          setValidatedChangeRequestAddress(lookupCrResults[index]);
        }
      }
    }
  }, [lookupCrResults, props.changeRequestAddress, isMountedRef]);

  useEffect(() => {
    // Update the compare status whenever the change request address or improved address changes.
    setCompareStatus(getCompareStatus());
  }, [props.changeRequestAddress, props.improvedAddress]);

  const postadresseCheckboxVisible =
    props.improvedAddress &&
    (!props.postadresse ||
      props.postadresse.formattedForOneLine !== props.improvedAddress.formattedForOneLine);

  // Check if the value from the change request has any errors
  useEffect(() => {
    if (props.changeRequestAddress) {
      const error = ChangeRequestValidator.hasBesoksAdresseErrors(
        props.changeRequestAddress,
        lookupCrResults,
      );
      if (error) {
        setCrValueError(error);
      } else {
        setCrValueError(null);
      }
    } else {
      setCrValueError(null);
    }
  }, [props.changeRequestAddress, lookupCrResults]);

  return (
    <div className={Style.addressFormContainer}>
      <div
        className={cx(Style.changeRequestRowHeaderContainer, {
          [Style.isNotExpandedRowContainer]: !isExpandRow,
        })}
        onClick={() => setIsExpandRow(!isExpandRow)}
      >
        <h6 className={Style.changeRequestRowHeader}>
          <span className={Style.changeRequestRowHeaderText}>Besøksadresse</span>
        </h6>
        <Button variant="link" size="sm">
          <IcebergIcon icon={isExpandRow ? 'chevron-up' : 'chevron-down'} bold />
        </Button>
      </div>
      <Collapse in={isExpandRow}>
        <div>
          <Row className={Style.addressFormRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <InputGroup size="sm">
                  <Form.Control
                    readOnly
                    value={formatAddressAsOneLiner(props.changeRequestAddress)}
                    isValid={
                      !isLoadingCrValidation && !crValueError && !!props.changeRequestAddress
                    }
                    isInvalid={!isLoadingCrValidation && !!crValueError}
                  />
                  <InputGroup.Text>
                    <CopyToClipboardIcon
                      val={formatAddressAsOneLiner(props.changeRequestAddress)}
                    />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {crValueError ?? 'Adresse ble ikke funnet (kjederegisteret)'}
                  </Form.Control.Feedback>
                  <Form.Control.Feedback type="valid">
                    Adresse funnet og verifisert (kjederegisteret)
                  </Form.Control.Feedback>
                </InputGroup>
                {isLoadingCrValidation && (
                  <Form.Text className="text-muted">Validerer adresse...</Form.Text>
                )}
                {activeEnheterOnBesoksadresse?.length > 0 && (
                  <OverlayTrigger
                    trigger="click"
                    placement="bottom"
                    rootClose
                    overlay={
                      <Popover id="active-enheter-popover">
                        <Popover.Header as="h3">Aktive enheter på adressen</Popover.Header>
                        <Popover.Body>
                          <ul style={{ paddingLeft: '1rem', marginBottom: 0 }}>
                            {[...activeEnheterOnBesoksadresse]
                              .sort((a, b) => (a.navn || '').localeCompare(b.navn || ''))
                              .map((e) => (
                                <li key={`${e.enhetId}-${e.lopenummer ?? 'x'}`}>
                                  <strong>
                                    <a
                                      href={`/enheter/${e.enhetId}`}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {e.lopenummer} - {e.navn || 'Ukjent navn'}
                                    </a>
                                  </strong>
                                  {e.hovedbransje ? ` – ${e.hovedbransje}` : ''}
                                </li>
                              ))}
                          </ul>
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <Button
                      variant="link"
                      className="p-0 text-muted"
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      Merk: Det finnes {activeEnheterOnBesoksadresse.length} aktive enheter på denne
                      adressen.
                    </Button>
                  </OverlayTrigger>
                )}
                {activeEnheterOnBesoksadresse?.length === 0 && !isLoadingCrValidation && (
                  <Form.Text className="text-muted">
                    Det finnes ingen aktive enheter på denne adressen.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              {!isLoadingCrValidation && !!crValueError ? (
                <CompareIcon compareStatus="ugyldig" tooltipText={crValueError} />
              ) : (
                <CompareIcon compareStatus={compareStatus} onClick={transferAddress} />
              )}
            </Col>
            <Col xs={12} md={5}>
              <Form.Group>
                <InputGroup size="sm">
                  {!props.improvedAddress ? (
                    <InputGroup.Text
                      style={{ lineHeight: 0, paddingLeft: '.25rem', paddingRight: '.25rem' }}
                    >
                      <IcebergIcon icon="search" />
                    </InputGroup.Text>
                  ) : (
                    <InputGroup.Text style={{ padding: '.15rem .15rem' }}>
                      <IcebergIcon icon="check" />
                    </InputGroup.Text>
                  )}
                  <AddressTypeahead
                    selected={props.improvedAddress}
                    clearTypeaheadInitiator={props.changeRequestId}
                    allowNew={true}
                    onChange={(adresse) => {
                      setCopyTouched((prev) => false);
                      props.onImprovedAddressChanged(adresse);
                    }}
                    size="sm"
                  />
                </InputGroup>
                {props.isCustomAddress && props.improvedAddress && (
                  <Form.Text className="text-muted">Egendefinert og gyldig adresse.</Form.Text>
                )}
                {!props.isCustomAddress && props.improvedAddress && (
                  <Form.Text className="text-muted">Adresse er funnet i kjederegisteret.</Form.Text>
                )}
              </Form.Group>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  disabled={!props.changeRequestAddress && !props.improvedAddress}
                  className={Style.showMapBtn}
                  variant="link"
                  size="sm"
                  onClick={() => setAddressMapVisible(!addressMapVisible)}
                >
                  {addressMapVisible ? 'Lukk kart' : 'Vis på kart'}
                </Button>
              </div>
              <div>
                {props.leveringsadresse && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ height: '25px', whiteSpace: 'nowrap' }}>
                      <strong>
                        Leveringsadresse: {props.leveringsadresse.formattedForOneLine}
                      </strong>
                    </span>
                  </div>
                )}
                {props.postadresse && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span
                      style={{
                        height: '25px',
                        whiteSpace: postadresseCheckboxVisible ? 'normal' : 'nowrap',
                      }}
                    >
                      <strong>
                        Postadresse:{' '}
                        {props.copyPostAdresse && props.improvedAddress
                          ? props.improvedAddress.formattedForOneLine
                          : props.postadresse.formattedForOneLine}
                      </strong>
                    </span>
                    {postadresseCheckboxVisible && (
                      <Form.Group controlId={`$postadresseCheckbox${props.changeRequestId}`}>
                        <Form.Check
                          className={Style.checkbox}
                          type="checkbox"
                          label="Kopier besøksadresse"
                          checked={props.copyPostAdresse}
                          onChange={() => {
                            setCopyTouched((prev) => true);
                            props.setCopyPostAdresse(!props.copyPostAdresse);
                          }}
                        />
                      </Form.Group>
                    )}
                  </div>
                )}
                {!props.postadresse && postadresseCheckboxVisible && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Form.Group controlId={`$postadresseCheckbox${props.changeRequestId}`}>
                      <Form.Check
                        className={Style.checkbox}
                        type="checkbox"
                        label="Kopier til postadresse"
                        checked={props.copyPostAdresse}
                        onChange={() => {
                          setCopyTouched((prev) => true);
                          props.setCopyPostAdresse(!props.copyPostAdresse);
                        }}
                      />
                    </Form.Group>
                  </div>
                )}
                {props.fakturaadresse && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ height: '25px', whiteSpace: 'nowrap' }}>
                      <strong>Fakturaadresse: {props.fakturaadresse.formattedForOneLine}</strong>
                    </span>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Collapse>
      {addressMapVisible && (
        <Row className={Style.addressMapsRow}>
          {props.changeRequestAddress && (
            <Col>
              <QAAddressMap
                coordinates={
                  crCoordinatesValidated && crCoordinatesValidated.isValid()
                    ? crCoordinatesValidated
                    : undefined
                }
                poststed={props.changeRequestAddress.poststed}
              />
              {crCoordinatesValidated?.isValid() && (
                <h6>Viser lokasjon for eksisterende adresse (venstre felt).</h6>
              )}
              {!crCoordinatesValidated?.isValid() && props.changeRequestAddress.poststed && (
                <h6>
                  Endringsmeldingens adresse (venstre felt) har ikke koordinater. Viser utsnitt av
                  poststed fra endringsmelding (venstre felt).
                </h6>
              )}
              {!crCoordinatesValidated?.isValid() && !props.changeRequestAddress.poststed && (
                <h6>Ingen lokasjon tilgjengelig.</h6>
              )}
            </Col>
          )}
          {props.improvedAddress && (
            <Col>
              <QAAddressMap
                coordinates={
                  improvedCoordinates && improvedCoordinates.isValid()
                    ? improvedCoordinates
                    : undefined
                }
                poststed={props.improvedAddress?.poststed}
              />
              {improvedCoordinates?.isValid() && (
                <h6>Viser lokasjon for eksisterende adresse (høyre felt).</h6>
              )}
              {!improvedCoordinates?.isValid() && props.improvedAddress?.poststed && (
                <h6>
                  Eksisterende adresse (høyre felt) har ikke koordinater. Viser utsnitt av poststed
                  fra adressen (høyre felt).
                </h6>
              )}
              {!improvedCoordinates?.isValid() && !props.improvedAddress?.poststed && (
                <h6>Ingen lokasjon tilgjengelig.</h6>
              )}
            </Col>
          )}
        </Row>
      )}
    </div>
  );
};

export default QAAddressForm;
